"use client";

import { Capacitor } from "@capacitor/core";
// Static: a plugin import is only a `registerPlugin` proxy, and a lazy chunk
// that fails to load mid-share makes the dev runtime reload the whole page.
import { Share } from "@capacitor/share";
import { HiiveInstants as HiiveMediaSave } from "./instants-plugin";
import { type ShareResult, shareViaSystemSheet } from "./share";
import { nativeShareAssetError } from "./share-asset-policy";

export type SavableMedia = {
  url: string;
  kind: "image" | "video";
  /** Without an extension; the file's own type picks one. */
  baseName: string;
};

/**
 * `photos`: in the device's photo library. `downloaded`: a browser download.
 * `shared`: an older app binary without the native video methods handed the
 * file to the share sheet, whose "Save Video" does the same job.
 */
export type SaveMediaOutcome = "photos" | "downloaded" | "shared" | "aborted";

export class MediaSaveError extends Error {
  constructor(
    message: string,
    readonly reason: "permission" | "too_large" | "failed",
  ) {
    super(message);
    this.name = "MediaSaveError";
  }
}

/** Formats the Photos library reliably imports. WebP and AVIF are not on it. */
const PHOTOS_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/gif",
]);

const EXTENSION_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.onload = () => {
      const value = String(reader.result ?? "");
      resolve(value.slice(value.indexOf(",") + 1));
    };
    reader.readAsDataURL(blob);
  });

function assertSavableFile(media: SavableMedia) {
  if (/\.m3u8(?:[?#]|$)/i.test(media.url)) {
    // A streaming playlist, not a file: saving it would write a text file.
    throw new MediaSaveError("This video can't be saved yet", "failed");
  }
}

async function fetchMediaBlob(media: SavableMedia) {
  assertSavableFile(media);
  const response = await fetch(media.url);
  if (!response.ok) throw new MediaSaveError("Couldn't load media", "failed");
  const blob = await response.blob();
  const type =
    blob.type && blob.type !== "application/octet-stream"
      ? blob.type
      : media.kind === "video"
        ? "video/mp4"
        : "image/jpeg";
  return blob.type === type ? blob : new Blob([blob], { type });
}

/** Re-encodes a WebP/AVIF feed image as JPEG so Photos accepts it. */
async function toPhotosImage(blob: Blob): Promise<Blob> {
  if (PHOTOS_IMAGE_TYPES.has(blob.type)) return blob;
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas unavailable");
    context.drawImage(bitmap, 0, 0);
    const jpeg = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    // Android returns null past its canvas limit without throwing.
    if (!jpeg) throw new Error("JPEG encode failed");
    return jpeg;
  } finally {
    bitmap.close();
  }
}

const fileNameFor = (baseName: string, type: string) =>
  `${baseName.replace(/[^a-zA-Z0-9._-]/g, "-")}.${EXTENSION_BY_TYPE[type] ?? "bin"}`;

const isPermissionError = (error: unknown) =>
  error instanceof Error && /denied|permission/i.test(error.message);

const isNotImplemented = (error: unknown) =>
  error instanceof Error &&
  (("code" in error && error.code === "UNIMPLEMENTED") ||
    /not implemented/i.test(error.message));

const isShareCancel = (error: unknown) =>
  error instanceof Error &&
  /abort|cancel/i.test(`${error.name} ${error.message}`);

const videoTypeFor = (url: string) =>
  /\.mov(?:[?#]|$)/i.test(url)
    ? "video/quicktime"
    : /\.webm(?:[?#]|$)/i.test(url)
      ? "video/webm"
      : "video/mp4";

/**
 * A video never crosses the bridge: the native side downloads it into the
 * app's cache and hands back a path. As base64 a clip sat in memory several
 * times over, and on the simulator that was enough to reload the web view.
 * Resolves null on a binary from before `downloadToCache`.
 */
async function downloadVideoNatively(media: SavableMedia) {
  assertSavableFile(media);
  const mimeType = videoTypeFor(media.url);
  const fileName = fileNameFor(media.baseName, mimeType);
  try {
    const { path } = await HiiveMediaSave.downloadToCache({
      url: media.url,
      fileName,
    });
    return { path, mimeType, fileName };
  } catch (error) {
    if (isNotImplemented(error)) return null;
    throw error;
  }
}

async function saveImageNatively(media: SavableMedia) {
  const blob = await fetchMediaBlob(media);
  if (nativeShareAssetError(blob) === "asset_too_large") {
    throw new MediaSaveError("This file is too large to save", "too_large");
  }
  const image = await toPhotosImage(blob);
  await HiiveMediaSave.saveImage({
    base64: await blobToBase64(image),
    mimeType: image.type,
    fileName: fileNameFor(media.baseName, image.type),
  });
  return "photos" as const;
}

async function saveVideoNatively(media: SavableMedia) {
  const download = await downloadVideoNatively(media);
  if (download) {
    await HiiveMediaSave.saveVideo(download);
    return "photos" as const;
  }
  // An older binary: the share sheet's "Save Video".
  const result = await shareMediaFile(media);
  if (result.ok) return "shared" as const;
  if (result.reason === "aborted") return "aborted" as const;
  throw new MediaSaveError("Couldn't save video", "failed");
}

function downloadInBrowser(media: SavableMedia, blob: Blob) {
  // An object URL, not the media URL: `download` is ignored cross-origin, so
  // the old anchor opened the file in a new tab instead of saving it.
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileNameFor(media.baseName, blob.type);
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  return "downloaded" as const;
}

/** Saves feed media to Photos in the app, or downloads it on the web. */
export async function saveMediaToDevice(
  media: SavableMedia,
): Promise<SaveMediaOutcome> {
  if (!Capacitor.isNativePlatform()) {
    try {
      return downloadInBrowser(media, await fetchMediaBlob(media));
    } catch (error) {
      if (error instanceof MediaSaveError) throw error;
      // A media host without CORS for this origin: open the file instead,
      // where the browser's own save works.
      window.open(media.url, "_blank", "noopener");
      return "downloaded";
    }
  }
  try {
    return media.kind === "video"
      ? await saveVideoNatively(media)
      : await saveImageNatively(media);
  } catch (error) {
    if (error instanceof MediaSaveError) throw error;
    if (isPermissionError(error)) {
      throw new MediaSaveError("Photo access is off for Hiive", "permission");
    }
    throw new MediaSaveError(
      media.kind === "video" ? "Couldn't save video" : "Couldn't save photo",
      "failed",
    );
  }
}

/**
 * Hands the media FILE (not a link) to the system share sheet, so WhatsApp,
 * Messages or AirDrop receive the photo or video itself.
 */
export async function shareMediaFile(
  media: SavableMedia,
): Promise<ShareResult> {
  if (media.kind === "video" && Capacitor.isNativePlatform()) {
    const download = await downloadVideoNatively(media);
    if (download) {
      try {
        await Share.share({
          files: [download.path],
          dialogTitle: "Share video",
        });
        return { ok: true };
      } catch (error) {
        if (isShareCancel(error)) return { ok: false, reason: "aborted" };
        return { ok: false, reason: "capacitor_share_failed" };
      }
    }
  }
  const blob = await fetchMediaBlob(media);
  const shareable =
    media.kind === "image" && blob.type !== "image/gif"
      ? // WhatsApp and Messages drop WebP/AVIF attachments on iOS.
        await toPhotosImage(blob).catch(() => blob)
      : blob;
  const extension = EXTENSION_BY_TYPE[shareable.type];
  const file = new File(
    [shareable],
    fileNameFor(media.baseName, shareable.type),
    {
      type: shareable.type,
    },
  );
  return shareViaSystemSheet({
    dialogTitle: media.kind === "video" ? "Share video" : "Share photo",
    asset: {
      file,
      // The asset type only routes the native temp file; jpeg/heic travel as
      // "png" the same way the chat viewer's shares always have.
      type:
        extension === "mp4" || extension === "webm"
          ? extension
          : media.kind === "video"
            ? "mp4"
            : "png",
    },
  });
}
